import React, { useState } from 'react';
import { MessagesSquare, ListChecks, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PeoplePane } from '../PeoplePane';
import { SessionStudio } from './SessionStudio';
import { SessionsView } from './SessionsView';

/*
 * The People area of one client, for its Solution Partner: who is in the
 * company, a new session built with the session agent, and every session
 * sent with its responses.
 */

export type PeopleTab = 'people' | 'new' | 'sessions';

const TABS: { key: PeopleTab; label: string; Icon: LucideIcon }[] = [
  { key: 'people', label: 'People', Icon: Users },
  { key: 'new', label: 'New session', Icon: MessagesSquare },
  { key: 'sessions', label: 'Sessions', Icon: ListChecks },
];

export const PeopleArea: React.FC<{
  clientId: string;
  initialTab?: PeopleTab;
  initialEmail?: string;
  initialSession?: string;
}> = ({ clientId, initialTab = 'people', initialEmail, initialSession = '' }) => {
  const [tab, setTab] = useState<PeopleTab>(initialTab);
  const [openSession, setOpenSession] = useState(initialSession);

  return (
    <div>
      <nav className="mb-5 flex flex-wrap gap-1 border-b border-line" role="tablist" aria-label="People">
        {TABS.map(({ key, label, Icon }) => {
          let tone = 'border-transparent text-ink-muted hover:text-ink';
          if (tab === key) tone = 'border-ink text-ink';
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => {
                setTab(key);
                if (key === 'sessions') setOpenSession('');
              }}
              className={`-mb-px inline-flex items-center gap-2 border-b-2 px-3.5 py-2.5 text-[13.5px] font-semibold transition-colors ${tone}`}
            >
              <Icon size={15} />
              {label}
            </button>
          );
        })}
      </nav>
      {tab === 'people' && <PeoplePane clientId={clientId} initialEmail={initialEmail} />}
      {tab === 'new' && (
        <SessionStudio
          clientId={clientId}
          onSent={(id) => {
            setOpenSession(id);
            setTab('sessions');
          }}
        />
      )}
      {tab === 'sessions' && <SessionsView clientId={clientId} openId={openSession} onOpen={setOpenSession} onCreate={() => setTab('new')} />}
    </div>
  );
};

export default PeopleArea;
